import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import useNextHotelBookingStay from 'hooks/Search/useNextHotelBookingStay'
import moment, { type Moment } from 'moment'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  formatDateRangeLabel,
  getThisWeekendRange,
} from 'lib/search/experiences'
import styled from 'styled-components'
import { rem } from 'polished'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'

const MultiLineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

export const CHIP_ANYTIME = 'anytime'
export const CHIP_TODAY = 'today'
export const CHIP_TOMORROW = 'tomorrow'
export const CHIP_WEEKEND = 'weekend'
export const CHIP_NEXT_BOOKING = 'next-booking'
export const CHIP_TRIP_DATES = 'trip-dates'

export type ExperiencesDateQuickChipValue =
  | typeof CHIP_ANYTIME
  | typeof CHIP_TODAY
  | typeof CHIP_TOMORROW
  | typeof CHIP_WEEKEND
  | typeof CHIP_NEXT_BOOKING
  | typeof CHIP_TRIP_DATES

/** Payload when a chip is selected: value + optional date range (omit range for Anytime). */
export interface ExperiencesDateQuickChipSelectPayload {
  value: ExperiencesDateQuickChipValue
  range?: { start: Moment, end: Moment }
}

export { getThisWeekendRange }

interface Props {
  /** Called with chip value and date range when applicable (Anytime has no range). Parent applies to context/state. */
  onSelect: (payload: ExperiencesDateQuickChipSelectPayload) => void
  /** Current check-in date; when provided with checkoutDate/dateSearchOptionId, highlights the matching chip. */
  checkinDate?: Moment
  /** Current check-out date. */
  checkoutDate?: Moment
  /** Current date search option (e.g. ANYTIME). Omit when no context/state (e.g. search page). */
  dateSearchOptionId?: string
  className?: string
  displayType?: 'single-line' | 'multi-line'
  showNextBooking?: boolean
  tripDateRange?: { start: Moment, end: Moment }
  /** When provided, chips whose date range falls entirely outside [minDate, maxDate] are hidden. */
  minDate?: Date
  maxDate?: Date
  /** When false, hides the Anytime chip. Defaults to true. */
  allowAnytime?: boolean
  showToday?: boolean
}

function ExperiencesSearchDateQuickChips(props: Props) {
  const {
    onSelect,
    checkinDate,
    checkoutDate,
    dateSearchOptionId,
    className,
    displayType = 'single-line',
    showNextBooking = true,
    tripDateRange,
    minDate,
    maxDate,
    allowAnytime = true,
    showToday = false,
  } = props
  const nextHotelStay = useNextHotelBookingStay()
  const intl = useIntl()

  const { anytimePlaceholder, todayLabel, tomorrowLabel, thisWeekendLabel } = useExperienceSearchLabels()
  const tripDateRangeStart = tripDateRange?.start
  const tripDateRangeEnd = tripDateRange?.end

  const chips = useMemo(() => {
    const isAvailable = (start: Moment, end: Moment) => {
      if (minDate && start.toDate() < minDate) return false
      if (maxDate && end.toDate() > maxDate) return false
      return true
    }

    const today = moment().startOf('day')
    const tomorrow = moment().add(1, 'day').startOf('day')
    const weekend = getThisWeekendRange()

    const base: Array<{
      value: ExperiencesDateQuickChipValue
      label: string
      startIcon: React.ReactElement | undefined
    }> = []
    if (allowAnytime) {
      base.push({
        value: CHIP_ANYTIME,
        label: anytimePlaceholder,
        startIcon: undefined,
      })
    }
    if (showToday && isAvailable(today, today)) {
      base.push({ value: CHIP_TODAY, label: todayLabel, startIcon: undefined })
    }
    if (isAvailable(tomorrow, tomorrow)) {
      base.push({ value: CHIP_TOMORROW, label: tomorrowLabel, startIcon: undefined })
    }
    if (isAvailable(weekend.start, weekend.end)) {
      base.push({ value: CHIP_WEEKEND, label: thisWeekendLabel, startIcon: undefined })
    }
    if (tripDateRangeStart && tripDateRangeEnd && isAvailable(tripDateRangeStart, tripDateRangeEnd)) {
      base.push({
        value: CHIP_TRIP_DATES,
        label: formatDateRangeLabel(tripDateRangeStart, tripDateRangeEnd, intl.locale),
        startIcon: <LineCalendarIcon size="S" />,
      })
    } else if (showNextBooking && nextHotelStay && isAvailable(nextHotelStay.checkIn, nextHotelStay.checkOut)) {
      base.push({
        value: CHIP_NEXT_BOOKING,
        label: formatDateRangeLabel(nextHotelStay.checkIn, nextHotelStay.checkOut, intl.locale),
        startIcon: <LineBedIcon size="S" />,
      })
    }
    return base
  }, [allowAnytime, showToday, nextHotelStay, anytimePlaceholder, todayLabel, tomorrowLabel, thisWeekendLabel, minDate, maxDate, intl.locale, showNextBooking, tripDateRangeEnd, tripDateRangeStart])

  const selectedChip = useMemo(() => {
    const hasSelection = checkinDate !== undefined || checkoutDate !== undefined || dateSearchOptionId !== undefined
    if (!hasSelection) {
      return undefined
    }
    if (
      allowAnytime &&
      !checkinDate &&
      !checkoutDate &&
      (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME || !dateSearchOptionId)
    ) {
      return CHIP_ANYTIME
    }
    if (checkinDate && checkoutDate) {
      if (
        tripDateRangeStart &&
        tripDateRangeEnd &&
        checkinDate.isSame(tripDateRangeStart, 'day') &&
        checkoutDate.isSame(tripDateRangeEnd, 'day')
      ) {
        return CHIP_TRIP_DATES
      }
      if (checkinDate.isSame(moment(), 'day') && checkoutDate.isSame(moment(), 'day')) {
        return CHIP_TODAY
      }
      if (checkinDate.isSame(moment().add(1, 'day'), 'day') && checkoutDate.isSame(moment().add(1, 'day'), 'day')) {
        return CHIP_TOMORROW
      }
      const { start, end } = getThisWeekendRange()
      if (checkinDate.isSame(start, 'day') && checkoutDate.isSame(end, 'day')) {
        return CHIP_WEEKEND
      }
      if (
        nextHotelStay &&
        checkinDate.isSame(nextHotelStay.checkIn, 'day') &&
        checkoutDate.isSame(nextHotelStay.checkOut, 'day')
      ) {
        return CHIP_NEXT_BOOKING
      }
    }
    return undefined
  }, [allowAnytime, checkinDate, checkoutDate, dateSearchOptionId, nextHotelStay, tripDateRangeEnd, tripDateRangeStart])

  const handleChipClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const value = e.currentTarget.dataset.value as ExperiencesDateQuickChipValue | undefined
    if (value === CHIP_ANYTIME || value === selectedChip) {
      if (allowAnytime || value === selectedChip) {
        onSelect({ value: CHIP_ANYTIME })
      }
      return
    }
    if (value === CHIP_TODAY) {
      const d = moment().startOf('day')
      onSelect({ value: CHIP_TODAY, range: { start: d, end: d } })
      return
    }
    if (value === CHIP_TOMORROW) {
      const d = moment().add(1, 'day').startOf('day')
      onSelect({ value: CHIP_TOMORROW, range: { start: d, end: d } })
      return
    }
    if (value === CHIP_WEEKEND) {
      onSelect({ value: CHIP_WEEKEND, range: getThisWeekendRange() })
      return
    }
    if (value === CHIP_NEXT_BOOKING && nextHotelStay) {
      onSelect({
        value: CHIP_NEXT_BOOKING,
        range: { start: nextHotelStay.checkIn, end: nextHotelStay.checkOut },
      })
      return
    }
    if (value === CHIP_TRIP_DATES && tripDateRangeStart && tripDateRangeEnd) {
      onSelect({
        value: CHIP_TRIP_DATES,
        range: { start: tripDateRangeStart, end: tripDateRangeEnd },
      })
    }
  }, [allowAnytime, nextHotelStay, onSelect, selectedChip, tripDateRangeEnd, tripDateRangeStart])

  const Container = displayType === 'single-line' ? HorizontalContainerWithFadedOverflow : MultiLineContainer

  return (
    <Container className={className} overflowGutter={16} gap={8}>
      {chips.map(chip => (
        <FilterChip
          key={chip.value}
          size="medium"
          selected={selectedChip === chip.value}
          data-value={chip.value}
          onClick={handleChipClick}
          startIcon={chip.startIcon}
        >
          {chip.label}
        </FilterChip>
      ))}
    </Container>
  )
}

export default ExperiencesSearchDateQuickChips
